import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { createFilterParams, fetchAuthenticatedForJson, fetchAuthenticatedWithoutResult, fetchWithoutAuth } from '../utils';
import { Artikel, ArtikelInput, Handleiding } from '../types/content';
import { DeelnemerOutput } from '../types/deelnemer';
import { PagedResponse, PaginationQueryParameters } from '../types';

const QUERY_KEY = ['config'];
const BASE_URL = import.meta.env.VITE_CONFIG_API_URL;

export const useGetArtikels = (authenticated = false, management = false): UseQueryResult<Artikel[]> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'artikels', authenticated, management],
    queryFn: () => {
      if (management) return fetchAuthenticatedForJson<Artikel[]>(`${BASE_URL}/artikels/management`);
      if (authenticated) return fetchAuthenticatedForJson<Artikel[]>(`${BASE_URL}/artikels`);
      return fetchWithoutAuth<Artikel[]>(`${BASE_URL}/artikels/public`);
    },
  });

export const useGetDeelnemers = (params?: PaginationQueryParameters<DeelnemerOutput>): UseQueryResult<PagedResponse<DeelnemerOutput>, Error> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'deelnemers', params],
    queryFn: () => fetchAuthenticatedForJson<PagedResponse<DeelnemerOutput>>(`${BASE_URL}/deelnemers?${createFilterParams(params ?? {})}`),
    enabled: !!params,
  });

export const useGetArtikelById = (id?: number): UseQueryResult<Artikel, Error> =>
  useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => fetchAuthenticatedForJson<Artikel>(`${BASE_URL}/artikel/${id}`),
    enabled: !!id,
  });

export const useCreateArtikel = () => {
  return useMutation<void, Error, ArtikelInput>({
    mutationFn: (newArtikel: ArtikelInput) =>
      fetchAuthenticatedWithoutResult(`${BASE_URL}/artikel`, {
        json: newArtikel,
        method: 'POST',
      }),
    mutationKey: [...QUERY_KEY],
  });
};

export const useUpdateArtikel = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, ArtikelInput>({
    mutationFn: (newArtikel: ArtikelInput) =>
      fetchAuthenticatedWithoutResult(`${BASE_URL}/artikel/${id}`, {
        json: newArtikel,
        method: 'PUT',
      }),
    mutationKey: [...QUERY_KEY, id],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, id] });
    },
  });
};

export const useDeleteArtikel = () => {
  return useMutation<void, Error, number>({
    mutationFn: (id: number) =>
      fetchAuthenticatedWithoutResult(`${BASE_URL}/artikel/${id}`, {
        method: 'DELETE',
      }),
    mutationKey: [...QUERY_KEY],
  });
};

export const useGetPublicHandleidingen = (): UseQueryResult<Handleiding[]> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'handleidingen', 'public'],
    queryFn: async () => {
      const data = await fetchWithoutAuth<Handleiding[]>(`${BASE_URL}/handleidingen/public`);
      return data.sort((a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime());
    },
  });

export const useGetVisibleHandleidingen = (enabled: boolean): UseQueryResult<Handleiding[]> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'handleidingen', 'visible'],
    queryFn: async () => {
      const data = await fetchAuthenticatedForJson<Handleiding[]>(`${BASE_URL}/handleidingen`);
      return data.sort((a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime());
    },
    enabled,
  });

export const useGetManagementHandleidingen = (): UseQueryResult<Handleiding[]> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'handleidingen', 'management'],
    queryFn: async () => {
      const data = await fetchAuthenticatedForJson<Handleiding[]>(`${BASE_URL}/handleidingen/management`);
      return data.sort((a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime());
    },
  });

export const useCreateHandleiding = () => {
  return useMutation<Handleiding, Error, Omit<Handleiding, 'id'>>({
    mutationFn: (newHandleiding: Omit<Handleiding, 'id'>) => {
      return fetchAuthenticatedForJson<Handleiding>(`${BASE_URL}/handleiding`, {
        json: newHandleiding,
        method: 'POST',
      });
    },
    mutationKey: [...QUERY_KEY],
  });
};

export const useGetCurrentHandleiding = (): UseQueryResult<Handleiding> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'handleiding'],
    queryFn: () => fetchAuthenticatedForJson<Handleiding>(`${BASE_URL}/handleiding`),
  });

export const useGetAll = (): UseQueryResult<Handleiding[]> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'handleidingen'],
    queryFn: async () => {
      const data = await fetchWithoutAuth<Handleiding[]>(`${BASE_URL}/handleidingen/public`);
      return data.sort((a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime());
    },
  });

export const useDeleteHandleiding = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id: number) =>
      fetchAuthenticatedForJson<void>(`${BASE_URL}/handleiding/${id}`, {
        method: 'DELETE',
      }),
    mutationKey: [...QUERY_KEY, 'handleiding-delete'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, 'handleidingen'] });
    },
  });
};

export const useUpdateHandleiding = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, Handleiding>({
    mutationFn: (handleiding: Handleiding) =>
      fetchAuthenticatedForJson<void>(`${BASE_URL}/handleiding/${handleiding.id}`, {
        json: handleiding,
        method: 'PUT',
      }),
    mutationKey: [...QUERY_KEY, 'handleiding-update'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, 'handleidingen'] });
    },
  });
};
